// ═══════════════════════════════════════════════════════════════════════════
// drpDecision.js — THE DRP decision rule. One home, four callers.
//
// The question this answers is operational and narrow:
//   "Is the DRP floor available today good enough that we should act, or wait?"
// It is NOT a milk-price forecast. It does not know the future and it does not
// identify the perfect day. It ranks TODAY'S ACTUAL LOCKABLE NET FLOOR against
// the offers this same coverage has actually made in our captured history.
//
// ── THE DEFECT THIS EXISTS FOR (measured 2026-08-28, live production) ───────
//
// The old 1-10 composite computed `execPctile` — today's state-specific net
// floor percentile within that quarter's own quote history, which is the single
// most decision-relevant number available — and then THREW IT AWAY. It was
// reported in a column and never entered the score. What drove the score
// instead were four CLASS-WIDE factors (settled-price level, seasonality, DMC
// margin, COT positioning), every one of which is identical for every open
// quarter of a class. Measured over the full stored history of drp_signal_daily:
//
//   New York, Class III, 2026-08-28:
//     2027-Q2   net floor $16.31   84.1st pctile   score 4.87
//     2027-Q3   net floor $16.47   18.1st pctile   score 4.87   <- same score
//
//   corr(score, exec_pctile)                        Class III  0.168
//   max exec_pctile spread inside ONE score value              0.941
//   cells where the STATE changed the score          0 of 516
//   distinct scores per (date,state,class)           2.60 across ~5 quarters
//
// So the headline repeated one market opinion across every quarter, sat 94
// percentile points away from the thing being bought, and was provably blind to
// the state whose premium it claimed to reflect.
//
// ── WHAT THIS MODULE REFUSES TO DO ─────────────────────────────────────────
//
//  - It takes NO market-regime input. COT, DMC margin, seasonality and settled
//    momentum are not parameters of `drpDecide`, so they CANNOT create a BUY —
//    not by weight, not by accident, not by a future edit that forgets. They
//    remain available as displayed CONTEXT, computed elsewhere.
//  - It never synthesises urgency. `salesWindow` is optional and defaults to
//    null; a missing verified deadline stays missing. There is no code path
//    from "we do not know the deadline" to "act now".
//  - It fails CLOSED. Missing inputs, a stale board, an unparseable quarter or
//    a thin history all return NO READ with the reason named.
//  - It is PURE. No fetch, no React, no Date.now(). `asOf` is an argument, so
//    the backtest replays history through the SHIPPED function rather than a
//    hand-copied approximation.
//
// PROVISIONAL. The thresholds below are starting hypotheses, not evidence.
// `THRESHOLD_VERSION` is a HASH of the threshold object, so changing any number
// changes the version automatically and stored rows can never silently mix
// formulas. See tools/drp-backtest.js for what the history can and cannot
// support.
// ═══════════════════════════════════════════════════════════════════════════

// The only import, and it is a sibling PURE rule module. Board health lives
// inside the decision rather than at the caller, because the defect it fixes WAS
// a caller passing the wrong date — a guard the caller can forget is not a guard.
const BH = require("./drpBoardHealth.js");

const MODEL_VERSION = "floor-quality-2";   // horizon-adjusted, board-health gated
// 2.0.0 (2026-10-15): BUY means "a strong floor that has TURNED DOWN FROM A
// RECENT HIGH", not "a strong floor below its all-window best"; START holds
// against WATCH inside a hysteresis margin; floor_move fires only while
// acting. A row written under 1.0.0 is not comparable and must never be the
// "from" side of a transition — see sameRuleVersion().
const DECISION_VERSION = "2.0.0";

// ── provisional thresholds ─────────────────────────────────────────────────
const DEFAULT_THRESHOLDS = {
  minObs: 20,               // distinct valid pricing dates required for any read
  startPctile: 0.75,        // "attractive floor" — cover the next tranche
  watchPctile: 0.55,        // below this is a poor entry for THIS coverage
  moveWindow: 5,            // pricing days for the short-term trend
  longWindow: 20,           // pricing days for the medium trend
  materialMovePctile: 0.70, // |5-day move| percentile that counts as "material"
  minMoveObs: 20,           // 5-day changes needed before a material move exists
  strongObs: 60,            // Data Quality: Strong at/above this many pricing days
  // ⚠ maxBoardAgeDays is GONE. It was a calendar-day limit of 4, which would let
  // a four-day-old board support an "act today" read, and it was measured against
  // asOf === boardDate so it read 0 forever. Freshness is trading SESSIONS now.
  maxDelayedSessions: 1,    // sessions behind before the decision surface is withheld
  // Horizon is controlled by MATCHING, not modelling — see horizonMatchedPctile
  // and the audit that rejected the fitted alternative.
  horizonBandWidths: [1, 2, 3], // months either side of today's horizon, widened only as needed
  minBandObs: 12,           // offers required inside the band before a rank is reported
  // ── PROVISIONAL, 2026-10-15 — chosen by Kevin, NOT tuned on the backtest ──
  // BUY's timing test is measured against the cell's RECENT high: the max net
  // floor over its last `recentHighWindow` pricing dates (as-of cut already
  // applied by buildFloorHistory). The old test compared against the best
  // floor over the whole 400-day window AT ANY HORIZON while the percentile
  // was horizon-matched, so a floor 9 months out set the bar and 34 of 41
  // stored BUYs fired on a floor that was rising strongly — 0 of 41 at a
  // genuine turn-down (audit 2026-10-15, NY Class III 2027-Q1: BUY after 13
  // straight rising sessions, "best" $16.5428 from 2026-04-27).
  recentHighWindow: 20,
  // Leaving START for WATCH needs the percentile BELOW startPctile by this
  // margin; entering START is unchanged. Measured churn before it: START->WATCH
  // 30 times and WATCH->START 12 times in 7 weeks.
  startExitMargin: 0.05,
};

// A stable, order-independent fingerprint of the thresholds actually used.
// Editing any number above changes THRESHOLD_VERSION with no second edit.
function thresholdVersion(t) {
  const s = Object.keys(t).sort().map((k) => `${k}=${t[k]}`).join(";");
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  return `t${h.toString(16)}`;
}

const DECISION = { BUY: "BUY", START: "START", WATCH: "WATCH", WAIT: "WAIT", NO_READ: "NO READ" };
const DATA_QUALITY = { STRONG: "Strong", LIMITED: "Limited", STALE: "Stale", INSUFFICIENT: "Insufficient" };

// ── canonical quarter key ──────────────────────────────────────────────────
// The board relabelled Q3-27 -> 2027-Q3 on 2026-03-19. Both forms must fold to
// one key or a quarter's history splits in half at that date and every
// percentile after it is computed over the wrong sample.
function parseDrpQuarter(label) {
  let m = /^Q([1-4])-(\d{2})$/.exec(label || "");
  if (m) return { q: +m[1], year: 2000 + +m[2] };
  m = /^(\d{4})-Q([1-4])$/.exec(label || "");
  if (m) return { q: +m[2], year: +m[1] };
  return null;
}
// null, never a passthrough of the raw label: an unparseable quarter must GATE
// the decision, not silently become its own one-row "history".
function drpQKey(label) {
  const pq = parseDrpQuarter(label);
  return pq ? `${pq.year}Q${pq.q}` : null;
}

// ── helpers ────────────────────────────────────────────────────────────────
const num = (v) => (v == null || v === "" || Number.isNaN(Number(v)) ? null : Number(v));
const dayDiff = (a, b) => Math.round((Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86400000);
// Money in integer hundredths of a cent. A $/cwt figure carries 4 decimals in
// drp_board and float subtraction of two of them drifts — the same measured
// trap as cashTargetBasis (5.25 - 4.97 = 0.28000000000000025).
const SCALE = 10000;
const qd = (v) => (v == null ? null : Math.round(v * SCALE));
const unqd = (v) => (v == null ? null : v / SCALE);
function pctileOf(sorted, v) { // share of history at or below v
  if (!sorted.length) return null;
  let c = 0;
  for (const x of sorted) if (x <= v) c++;
  return c / sorted.length;
}
function quantile(sortedAsc, p) {
  if (!sortedAsc.length) return null;
  const i = Math.min(sortedAsc.length - 1, Math.max(0, Math.ceil(p * sortedAsc.length) - 1));
  return sortedAsc[i];
}

// ── history builder ────────────────────────────────────────────────────────
// ONE row per pricing date. The board carries several milk_yield_id rows per
// (state, class, quarter, date); they are the same offer at different declared
// yields, so counting ROWS instead of DATES inflates n several-fold and makes a
// thin cell look strong enough to gate through.
//
// `asOf` truncates: nothing dated after it may inform a decision made on it.
// That is what makes the backtest leak-proof — the cut is enforced HERE, in the
// shipped path, not in the harness that calls it.
// ⚠ A REQUESTED STATE FILTER OVER ROWS THAT CARRY NO state_code IS A BUG, AND IT
// FAILS SILENTLY AS AN EMPTY HISTORY. That is exactly what happened on the first
// live run of this module: the backend's board query did not SELECT state_code,
// every row compared "undefined" !== "36", and all 30 cells wrote n_obs = 0 /
// NO READ while the offline fixture — which DID carry the column — read 175.
// The repo has this recorded already ("the column has to be SELECTED, not just
// accepted", grain price_election_pct). An empty history and a mis-typed query
// must not look the same, so this returns a NAMED error instead of [].
function buildFloorHistory(boardRows, { state, milkClass, quarter, asOf }) {
  const key = drpQKey(quarter);
  if (!key) return { key: null, byDate: [], series: [], error: null };
  const rows = boardRows || [];
  if (state != null && rows.length && !rows.some((r) => r && Object.prototype.hasOwnProperty.call(r, "state_code"))) {
    return { key, byDate: [], series: [], error: "board rows carry no state_code column — the query must SELECT it" };
  }
  const byDate = new Map();
  for (const r of rows) {
    if (state != null && String(r.state_code) !== String(state)) continue;
    if (String(r.milk_class) !== String(milkClass)) continue;
    if (drpQKey(r.quarter) !== key) continue;
    const nf = num(r.net_floor);
    if (nf == null) continue;
    const d = String(r.price_date).slice(0, 10);
    if (asOf && d > asOf) continue;
    byDate.set(d, nf);
  }
  const dates = Array.from(byDate.keys()).sort();
  // Each observation carries HOW FAR OUT it was quoted. Without that the
  // percentile silently mixes an offer made 16 months before the quarter with
  // one made 8 months before, and those are not the same opportunity — see
  // horizonAdjustedPctile below for the measurement.
  const pq = parseDrpQuarter(quarter);
  const covStartYm = pq ? pq.year * 12 + (pq.q - 1) * 3 : null;   // first month of the covered quarter
  const monthsOutOf = (d) => (covStartYm == null ? null
    : covStartYm - (Number(d.slice(0, 4)) * 12 + (Number(d.slice(5, 7)) - 1)));
  return {
    key,
    byDate: dates.map((d) => ({ date: d, netFloor: byDate.get(d), monthsOut: monthsOutOf(d) })),
    series: dates.map((d) => byDate.get(d)),
    error: null,
  };
}

// ── horizon adjustment ─────────────────────────────────────────────────────
// ⚠ THE RAW PERCENTILE IS CONTAMINATED BY HORIZON, AND IT IS NOT SUBTLE.
// Measured on the live board (NY, all quarters with >= 30 observations):
//
//   corr(premium, months_out)    Class III  +0.963    Class IV  +0.831
//   corr(net_floor, months_out)  Class III  -0.238    Class IV  -0.528
//
// Producer premium is very nearly a deterministic function of how far out the
// quarter is — it decays as the quarter approaches (NY Class III 2027-Q2:
// $0.528/cwt at 16 months out, $0.411 at 8). Since net floor is
// `floor - premium`, that decay MECHANICALLY lifts the net floor as a quarter
// ages. So "today sits at the 100th percentile of this quarter's captured
// offers" can mean nothing more than "today is late in this quarter's life" —
// which is exactly what Class IV was reporting: every open quarter at the 91st
// to 100th percentile at once.
//
// The fix is to rank the RESIDUAL after removing the cell's own horizon trend:
// "is this a good offer FOR THIS POINT in the quarter's life?" It needs a real
// spread of horizons to fit, so it reports null rather than a fit on a stub, and
// the raw percentile is always reported beside it.
// ⚠ A FITTED DETREND WAS TRIED FIRST AND FAILED ITS OWN VALIDATION.
// The obvious control is to regress net floor on months_out per cell and rank
// the residual. It was built, it moved live readings a long way (Class IV
// 2027-Q1: 99th raw -> 55th), and `tools/drp-horizon-audit.js` then showed the
// movement was an artifact of the fit rather than a correction:
//
//   slope sign across quarters   Class III  BOTH (+0.071 .. -0.109)
//                                Class IV   BOTH (+0.222 .. -0.845)
//   slope CV across cells        3.78 (III), 2.91 (IV)
//   first-half vs second-half b  ratios of -80.5, +7.7, -10.3, +0.01
//
// A slope that changes sign between quarters and bears no relation to itself
// across one cell's own life is fitting noise, and subtracting noise
// MANUFACTURES rank. The assumption-free control disagreed with it flatly:
// ranked only against offers quoted at a SIMILAR horizon (33-51 observations
// per band), Class IV was still at the 94th-100th percentile, not the 42nd.
//
// So the horizon is controlled by MATCHING, not modelling. No functional form,
// nothing to be unstable, and the sample it used is reported so the reader can
// see what the rank was computed over. Robustness: +/-1mo and +/-2mo bands agree
// within 6 percentile points on every live cell.
function horizonMatchedPctile(hist, today, opts) {
  const { bandWidths, minBandObs } = opts;
  const tx = today.monthsOut;
  if (tx == null) return { pctile: null, reason: "today's horizon not resolvable" };
  const pts = hist.filter((h) => h.monthsOut != null);
  if (pts.length !== hist.length) return { pctile: null, reason: "horizon not resolvable for every observation" };
  // Widen the band only as far as needed to clear the observation floor, and
  // say which width was used — a rank over 9 offers and a rank over 51 are
  // different statements.
  for (const w of bandWidths) {
    const inBand = pts.filter((p) => Math.abs(p.monthsOut - tx) <= w);
    if (inBand.length < minBandObs) continue;
    const vals = inBand.map((p) => p.netFloor).sort((a, b) => a - b);
    let c = 0;
    for (const v of vals) if (v <= today.netFloor + 1e-12) c++;
    return { pctile: c / vals.length, bandMonths: w, bandN: inBand.length, todayMonthsOut: tx, reason: null };
  }
  const widest = bandWidths[bandWidths.length - 1];
  const got = pts.filter((p) => Math.abs(p.monthsOut - tx) <= widest).length;
  return { pctile: null, todayMonthsOut: tx,
    reason: `only ${got} offers quoted within ${widest} months of this horizon (need ${minBandObs})` };
}

// ── THE DECISION ───────────────────────────────────────────────────────────
// Inputs are FACTS about one state x class x quarter as of one date. There is
// deliberately no `regime`, no `cot`, no `dmc` and no `seasonal` parameter.
function drpDecide(input) {
  const t = { ...DEFAULT_THRESHOLDS, ...(input.thresholds || {}) };
  const versions = {
    model_version: MODEL_VERSION,
    decision_version: DECISION_VERSION,
    threshold_version: thresholdVersion(t),
  };

  const asOf = input.asOf ? String(input.asOf).slice(0, 10) : null;
  const boardDate = input.boardDate ? String(input.boardDate).slice(0, 10) : null;
  const quarter = input.quarter;
  const qkey = drpQKey(quarter);

  const netFloor = num(input.netFloor);
  const fwdPrice = num(input.fwdPrice);
  const premium = num(input.premium);
  const floor = num(input.floor);

  const hist = input.history || [];   // [{date, netFloor}] ascending, includes today
  const n = hist.length;
  const series = hist.map((h) => h.netFloor);
  const firstDate = n ? hist[0].date : null;
  const lastDate = n ? hist[n - 1].date : null;
  const boardAgeDays = (asOf && boardDate) ? dayDiff(asOf, boardDate) : null;

  const base = {
    state: input.state == null ? null : String(input.state),
    milkClass: input.milkClass == null ? null : String(input.milkClass),
    quarter, qkey, asOf, boardDate, boardAgeDays,
    fwdPrice, floor, premium, netFloor,
    n, firstDate, lastDate,
    pctile: null, bestFloor: null, dollarsBelowBest: null, recentBest: null, dollarsBelowRecentBest: null,
    chg5: null, chg20: null, materialMove: null,
    floorQuality: null, timing: null,
    versions,
  };

  // ── BOARD HEALTH — trading sessions, not calendar days ──────────────────
  // `boardAgeDays` alone could never carry this. It was computed as
  // asOf - boardDate, and the snapshot writer passed the SAME value for both,
  // so it read 0 forever. Worse, a calendar-day limit of 4 would treat a
  // four-day-old board as fit to support an "act today" read. Health is judged
  // in expected trading SESSIONS and it degrades in stages.
  const health = BH.boardHealth({ asOf, boardDate, nowUtcHour: input.nowUtcHour,
    maxDelayedSessions: t.maxDelayedSessions,
    // A PROVEN PROMOTION FAILURE is board unhealth even when the dates line up.
    // Upstream rows landed and nothing was promoted; the board may look current
    // by date while being wrong by content, so the caller passes it in from
    // drp_board_runs and it forces the state down.
    promotionFailed: input.promotionFailed === true,
  });

  // ── TWO DIFFERENT GATES, AND CONFLATING THEM IS THE MISTAKE ─────────────
  // `blocking` means we cannot READ this cell at all — a missing input, an
  // unparseable quarter, a history too thin to rank against. There is no number
  // to show.
  //
  // Board unhealth is NOT that. The quote exists and its rank is computable; it
  // is simply not safe to call it TODAY'S. So an unhealthy board suppresses
  // ACT-TODAY ATTENTION and labels the reading with the date it belongs to,
  // rather than blanking a screen that has real information on it.
  const blocking = [];
  let staleFlag = false;
  if (input.historyError) blocking.push(String(input.historyError));
  if (!qkey) blocking.push(`quarter label "${quarter}" is not a canonical DRP quarter`);
  if (netFloor == null) blocking.push("no net floor on today's quote");
  if (fwdPrice == null) blocking.push("no forward expected price on today's quote");
  if (premium == null) blocking.push("no producer premium on today's quote");
  // A quote is only current if it IS the newest board row. A partial ingest
  // leaves this cell behind the board even when the board itself is fine.
  if (lastDate && boardDate && lastDate !== boardDate) { blocking.push(`newest quote for this coverage is ${lastDate}, board is ${boardDate}`); staleFlag = true; }
  if (n < t.minObs) blocking.push(`only ${n} valid pricing ${n === 1 ? "date" : "dates"} (need ${t.minObs})`);
  if (health.state === BH.BOARD_HEALTH.UNKNOWN) blocking.push(health.reason);

  if (blocking.length) {
    return {
      ...base,
      decision: DECISION.NO_READ,
      attention: "NO READ", action: "NO READ",
      attentionPriority: null, actionPriority: null,
      attentionNote: null, actionNote: null,
      boardHealth: health,
      usability: health.state === BH.BOARD_HEALTH.STALE ? "STALE" : "UNKNOWN",
      farmFit: input.farmFit || { state: "NOT_EVALUATED", reason: "decision withheld" },
      dataQuality: {
        status: staleFlag ? DATA_QUALITY.STALE : DATA_QUALITY.INSUFFICIENT,
        reasons: blocking,
        detail: `${n} valid pricing ${n === 1 ? "date" : "dates"}` + (boardDate ? `; board through ${boardDate}` : ""),
      },
      why: blocking[0].charAt(0).toUpperCase() + blocking[0].slice(1) + ".",
    };
  }

  // ── FLOOR QUALITY: how attractive is the net floor available today? ──────
  const sorted = series.slice().sort((a, b) => a - b);
  const rawPctile = pctileOf(sorted, netFloor);
  // The ALL-WINDOW best is CONTEXT ONLY. It spans every horizon this quarter
  // was ever quoted at, so it is not comparable to a horizon-matched rank and
  // it may not decide timing — that is the 2026-10-15 defect.
  const bestFloor = sorted[sorted.length - 1];
  const dollarsBelowBest = unqd(qd(bestFloor) - qd(netFloor));
  // The RECENT high: the cell's own last `recentHighWindow` pricing dates,
  // today included. `hist` is already as-of cut, so nothing after asOf can
  // reach it.
  const recentSlice = series.slice(-Math.max(1, t.recentHighWindow));
  const recentBest = recentSlice.reduce((m, v) => (v > m ? v : m), -Infinity);
  const dollarsBelowRecentBest = unqd(qd(recentBest) - qd(netFloor));

  // The horizon-controlled rank. Where it is computable it is the one that
  // answers the question the page claims to answer; the raw rank stays visible
  // beside it because they disagree on purpose and the difference is the
  // finding, not an error.
  // ⚠ RANK THE VALUE WE WERE HANDED, not the tail of the history array. In
  // production the caller passes the same number, so this agreed by luck; a
  // checker fixture that overrode netFloor proved the adjustment was ignoring
  // it entirely — the same "two sources for one number" shape as every other
  // defect in this file.
  const todayPoint = { netFloor, monthsOut: hist[n - 1] ? hist[n - 1].monthsOut : null };
  const hz = horizonMatchedPctile(hist, todayPoint, { bandWidths: t.horizonBandWidths, minBandObs: t.minBandObs });
  const pctile = hz.pctile != null ? hz.pctile : rawPctile;
  const pctileBasis = hz.pctile != null ? "horizon-matched" : "life-to-date";

  // ── TIMING: is that floor strengthening, stable, or rolling over? ────────
  const idx = n - 1;
  const at = (back) => (idx - back >= 0 ? hist[idx - back].netFloor : null);
  const prev5 = at(t.moveWindow), prev20 = at(t.longWindow);
  const chg5 = prev5 != null ? unqd(qd(netFloor) - qd(prev5)) : null;
  const chg20 = prev20 != null ? unqd(qd(netFloor) - qd(prev20)) : null;

  // A "material" move is derived from THIS coverage's own distribution of
  // 5-pricing-day changes, never a hardcoded cent figure. Too few changes to
  // form a distribution means no material-move threshold EXISTS, and BUY loses
  // its trend route rather than inventing one.
  const moves = [];
  for (let i = t.moveWindow; i < n; i++) moves.push(Math.abs(unqd(qd(hist[i].netFloor) - qd(hist[i - t.moveWindow].netFloor))));
  const materialMove = moves.length >= t.minMoveObs
    ? quantile(moves.slice().sort((a, b) => a - b), t.materialMovePctile)
    : null;

  // ── verified sales-window urgency — ABSENT UNLESS SOURCED ────────────────
  // There is no tested authoritative sales-deadline table in this database, so
  // this is null in production today. It is an INPUT, never a derivation: a
  // missing deadline can never become urgency.
  const sw = input.salesWindow || null;
  const deadlineUrgent = !!(sw && sw.verified === true
    && Number.isFinite(sw.daysRemaining) && Number.isFinite(sw.urgentWithinDays)
    && sw.daysRemaining >= 0 && sw.daysRemaining <= sw.urgentWithinDays);

  // ── decision ────────────────────────────────────────────────────────────
  const attractive = pctile >= t.startPctile;
  // `offBest` is kept as DISPLAYED CONTEXT (all-window best). It no longer
  // reaches the decision.
  const offBest = materialMove != null && dollarsBelowBest >= materialMove;
  const risingStrongly = materialMove != null && chg5 != null && chg5 >= materialMove;
  // ⚠ BUY's timing test: down from the RECENT high by at least the cell's own
  // material move, AND not rising strongly. Both halves are required — a floor
  // can sit a material distance below a high made 15 sessions ago while
  // climbing hard back toward it, and that is not a turn.
  const offRecentHigh = materialMove != null && dollarsBelowRecentBest >= materialMove;
  const turnedDown = offRecentHigh && !risingStrongly;
  const rollingOver = turnedDown;
  // ── START/WATCH hysteresis ───────────────────────────────────────────────
  // Entering START is today's rule. LEAVING it needs the rank to fall below
  // startPctile by `startExitMargin`. The previous state is an INPUT — this
  // function stays pure — and the caller passes it only from a row written
  // under the SAME rule versions (sameRuleVersion). BUY counts as being in the
  // band too: BUY -> WATCH crosses the same edge. A held cell is START, never
  // BUY — BUY always needs the full threshold.
  const prevDecision = input.previousDecision == null ? null : String(input.previousDecision);
  const wasActing = prevDecision === DECISION.START || prevDecision === DECISION.BUY;
  // 1e-9 absorbs float dust in the subtraction (0.75 - 0.05), nothing more.
  const heldByHysteresis = !attractive && wasActing && pctile + 1e-9 >= t.startPctile - t.startExitMargin;

  const money = (v) => (v == null ? "—" : `$${v.toFixed(2)}`);
  const ord = (p) => {
    const k = Math.round(p * 100), s = ["th", "st", "nd", "rd"], v = k % 100;
    return `${k}${s[(v - 20) % 10] || s[v] || s[0]}`;
  };
  const pctTxt = `${ord(pctile)} percentile of ${n} captured offers (${pctileBasis})`;

  let decision, why;
  if (attractive && (turnedDown || deadlineUrgent)) {
    decision = DECISION.BUY;
    const bits = [];
    if (turnedDown) bits.push(`turned down ${money(dollarsBelowRecentBest)} from its high of the last ${recentSlice.length} pricing days`);
    if (deadlineUrgent) bits.push(`sales window closes in ${sw.daysRemaining} days`);
    why = `${money(netFloor)} net floor; ${pctTxt}. ${bits.join(" and ")}.`;
  } else if (attractive || heldByHysteresis) {
    decision = DECISION.START;
    const tail = heldByHysteresis
      ? `. Held at START: within ${Math.round(t.startExitMargin * 100)} points of the attractive threshold after a ${prevDecision}.`
      : risingStrongly ? ". Attractive floor, but still improving."
      : dollarsBelowRecentBest === 0 ? ". Attractive floor, at its recent high."
      : ". Attractive floor.";
    why = `${money(netFloor)} net floor; ${pctTxt}${tail}`;
  } else if (pctile >= t.watchPctile) {
    decision = DECISION.WATCH;
    why = `${money(netFloor)} net floor; ${pctTxt}${chg5 != null && chg5 > 0 ? " and rising." : "."}`;
  } else {
    decision = DECISION.WAIT;
    why = `${money(netFloor)} net floor; ${pctTxt}.`;
  }

  // ── Data Quality — a factual status, never a fake "confidence %" ─────────
  const dqReasons = [];
  let status = DATA_QUALITY.STRONG;
  if (n < t.strongObs) {
    status = DATA_QUALITY.LIMITED;
    dqReasons.push(`${n} pricing dates — provisional, limited history`);
  }
  if (materialMove == null) {
    status = DATA_QUALITY.LIMITED;
    dqReasons.push(`too few ${t.moveWindow}-day changes to derive a material-move threshold; BUY cannot be triggered on trend`);
  }
  if (!sw || sw.verified !== true) dqReasons.push("no verified sales-window source; deadline urgency not used");
  if (hz.pctile == null) dqReasons.push(`no horizon-matched sample (${hz.reason}) — rank is life-to-date, which drifts up as a quarter ages`);
  if (health.state === BH.BOARD_HEALTH.DELAYED) dqReasons.push(health.reason);

  // ── WHAT THE AGENT IS ACTUALLY TOLD ─────────────────────────────────────
  // ⚠ THE ENGINE HAS NO FARM ECONOMICS IN IT. It knows the board, at 95%
  // coverage and protection factor 1.0, and nothing about this dairy's milk,
  // components, feed cost, existing coverage or margin. "BUY" is therefore a
  // claim it cannot support: it would read as "Silas has determined this dairy
  // should purchase DRP" when the honest statement is "this coverage quarter
  // deserves a look".
  //
  // The internal state machine KEEPS BUY/START/WATCH/WAIT — alert transitions,
  // stored history and the backtest are all keyed to it, and renaming a state
  // machine to fix a label is how two vocabularies drift apart. What changes is
  // the surface: `action` is what a human is shown, `decision` is what the
  // machine ran on, and the mapping is one place.
  // ⚠ ATTENTION IS NOT ADVICE, AND "HOLD" WAS ADVICE.
  // Layer A knows the board and nothing about this dairy. A weak generic floor
  // is a reason not to RAISE the quarter for review; it is NOT a reason for a
  // farm to skip DRP — a dairy with no coverage and real exposure may need this
  // quarter at any floor quality. "HOLD" read as "don't buy", which Layer A has
  // no standing to say. The third state is NONE: nothing to bring up today.
  //
  // Farm-specific action comes from Layer B, which does not exist yet.
  const ATTENTION_OF = {
    [DECISION.BUY]: { attention: "REVIEW", priority: "high", note: "strong floor that has turned down from a recent high" },
    [DECISION.START]: { attention: "REVIEW", priority: "normal", note: "floor is strong for this horizon" },
    [DECISION.WATCH]: { attention: "MONITOR", priority: null, note: "approaching the strong band" },
    [DECISION.WAIT]: { attention: "NONE", priority: null, note: "nothing to raise on floor quality today" },
  };
  let act = ATTENTION_OF[decision] || { attention: "NONE", priority: null, note: null };

  // ── THE RUNTIME GATE: no trustworthy current board, no current attention ──
  // Floor quality below is still computed and shown — it is a real reading of a
  // real quote — but it is the reading AS OF the board date, and nothing may
  // ask an agent to act on it today. The internal decision is forced to NO READ
  // as well, which is what keeps alert transitions from firing on stale data.
  let attentionSuppressedBy = null, suppressedAttention = null;
  if (!health.actionable) {
    attentionSuppressedBy = health.reason;
    suppressedAttention = act.attention;
    act = { attention: "NO READ", priority: null, note: `as of ${boardDate || "—"} — not today's board` };
    decision = DECISION.NO_READ;
    dqReasons.push(`act-today attention suppressed: ${attentionSuppressedBy}`);
  }

  // ── FLOOR QUALITY — five descriptive bands, about the FLOOR only ─────────
  // Never a verdict on a purchase, and deliberately worded so that the bottom
  // band says the floor is weak, not that the coverage is wrong.
  const fqLabel = pctile >= 0.90 ? "STRONG"
    : pctile >= t.startPctile ? "ABOVE TYPICAL"
    : pctile >= t.watchPctile ? "TYPICAL"
    : pctile >= 0.25 ? "BELOW TYPICAL"
    : "WEAK";

  // ── decision usability, the §11 contract state ───────────────────────────
  const usability = health.state === BH.BOARD_HEALTH.UNKNOWN ? "UNKNOWN"
    : health.state === BH.BOARD_HEALTH.STALE ? "STALE"
    : (n < t.strongObs || hz.pctile == null || health.state === BH.BOARD_HEALTH.DELAYED) ? "PARTIAL"
    : "USABLE";

  return {
    ...base,
    pctile, rawPctile, pctileBasis,
    horizonBandMonths: hz.bandMonths ?? null, horizonBandN: hz.bandN ?? null,
    monthsOut: hz.todayMonthsOut ?? null,
    bestFloor, dollarsBelowBest, recentBest, dollarsBelowRecentBest, chg5, chg20, materialMove,
    previousDecision: prevDecision, heldByHysteresis,
    boardHealth: health, usability,
    // `attention` is the agent-facing state (REVIEW / MONITOR / NONE).
    // `action` is kept as an alias ONLY so nothing downstream breaks mid-branch;
    // it carries the same three values and no prescriptive fourth.
    attention: act.attention, action: act.attention,
    attentionSuppressedBy, suppressedAttention,
    attentionPriority: act.priority, actionPriority: act.priority,
    attentionNote: act.note, actionNote: act.note,
    // FARM FIT IS A SEPARATE LAYER AND IT IS NOT BUILT. Saying so is the point:
    // an unevaluated margin must never be silently read as an acceptable one.
    farmFit: input.farmFit || { state: "NOT_EVALUATED", reason: "no farm economics supplied to this layer" },
    // ⚠ A STRONG GENERIC FLOOR IS NOT A FARM ACTION.
    // Class IV reads STRONG on every open quarter right now, and the book holds
    // ZERO Class IV endorsements (0 of 266). That is market context worth
    // showing — historical non-adoption is not evidence the product is
    // irrelevant — but it is NOT grounds to raise a farm-level action for a
    // dairy whose Class IV relevance nobody has established. Layer A therefore
    // never emits farm action; it emits ATTENTION on a market cell. This flag
    // is what a future Layer B must satisfy before any per-farm prompt.
    farmActionEligible: false,
    farmActionBlockedBy: input.farmClassRelevance == null
      ? `farm ${input.milkClass || "class"} relevance not established`
      : "farm economics not evaluated",
    floorQuality: {
      pctile, rawPctile, basis: pctileBasis, attractive, label: fqLabel,
      // The board prices ONE structure. Every figure here is at 95% coverage and
      // protection factor 1.0, which is NOT what the book buys (measured on
      // drp_endorsements: mean PF 1.45 across 266 endorsements).
      structure: { coverageLevel: 0.95, protectionFactor: 1.0, basis: "board convention" },
    },
    timing: {
      chg5, chg20, dollarsBelowBest, recentBest, dollarsBelowRecentBest, recentHighWindow: recentSlice.length,
      materialMove, rollingOver, offBest, offRecentHigh, turnedDown, risingStrongly, deadlineUrgent, heldByHysteresis,
      // ⚠ THE LABEL IS DIRECTION, NOT `rollingOver`. Those are different claims
      // and the first screenshot of this page proved it: `rollingOver` is true
      // whenever the floor sits a material distance BELOW its best, which is
      // trivially true for a poor entry — so every Class III row, including one
      // at the 5th percentile and $1.39 off its best, read "Rolling over". That
      // describes a floor that has been down for months as though it just
      // turned. `rollingOver` is a BUY CONFIRMATION and only means anything on
      // a floor that is already attractive; the column says which way the last
      // five pricing days actually went.
      label: risingStrongly ? "Rising"
        : chg5 == null ? "—"
        : chg5 > 0 ? "Firm" : chg5 < 0 ? "Softening" : "Flat",
      // shown only where it is load-bearing: an attractive floor coming off its high
      offBestNote: (attractive && turnedDown) ? `off its recent high by $${dollarsBelowRecentBest.toFixed(2)}` : null,
    },
    dataQuality: {
      status, reasons: dqReasons,
      detail: `${n} valid pricing dates (${firstDate} → ${lastDate}); board current through ${boardDate}.`,
    },
    decision, why,
  };
}

// ── alert transitions ──────────────────────────────────────────────────────
// Alerts fire on a CHANGE OF STATE, never on "the cron ran again". The daily
// snapshot re-evaluates every cell every day; without this, a cell that sits at
// START for six weeks emails six weeks of identical mail and the next real
// transition arrives in a folder nobody opens.
//
// Downgrades (BUY -> WATCH/WAIT) are RECORDED and not emailed: the agent needs
// the history to be complete, but an opportunity going away is not an action.
const ALERT = {
  ENTER_BUY: "enter_buy",
  ENTER_START: "enter_start",
  FLOOR_MOVE: "floor_move",
  DATA_STALE: "data_stale",
  DOWNGRADE: "downgrade",     // recorded, never emailed
};

// ⚠ A VERSION CHANGE IS NOT A TRANSITION. A row written under another
// model/decision/threshold version answered a different question; comparing it
// with today's turns every rule change into a burst of "transitions" nobody's
// market caused. Versions are read from `versions` (a drpDecide result) or the
// flat stored columns (a drp_decision_daily row). A prior with NO versions at
// all is treated as a different rule — refusing costs one missed transition,
// accepting could fabricate one.
function versionsOf(x) {
  if (!x) return null;
  const v = x.versions || x;
  if (v.decision_version == null || v.threshold_version == null) return null;
  return { model_version: v.model_version == null ? null : String(v.model_version),
    decision_version: String(v.decision_version), threshold_version: String(v.threshold_version) };
}
function sameRuleVersion(a, b) {
  const va = versionsOf(a), vb = versionsOf(b);
  if (!va || !vb) return false;
  return va.decision_version === vb.decision_version
    && va.threshold_version === vb.threshold_version
    && va.model_version === vb.model_version;
}
// The previous decision a caller may feed back into drpDecide for hysteresis:
// only from a row written under the CURRENT rule, else none.
function previousDecisionInput(prevRow, thresholds) {
  if (!prevRow || !prevRow.decision) return null;
  const t = { ...DEFAULT_THRESHOLDS, ...(thresholds || {}) };
  const cur = { model_version: MODEL_VERSION, decision_version: DECISION_VERSION, threshold_version: thresholdVersion(t) };
  return sameRuleVersion(prevRow, cur) ? String(prevRow.decision) : null;
}

function drpAlertTransition(prev, next, opts = {}) {
  const t = { ...DEFAULT_THRESHOLDS, ...(opts.thresholds || {}) };
  const acted = (d) => d === DECISION.BUY || d === DECISION.START;
  // A prior from another rule version is no prior at all.
  if (prev && next && !sameRuleVersion(prev, next)) {
    return { kind: null, email: false, record: false,
      from: prev.decision || null, to: next.decision || null, floorMove: null,
      reason: "rule version changed — not a transition", versionChanged: true };
  }
  const from = prev && prev.decision ? prev.decision : null;
  const to = next && next.decision ? next.decision : null;
  const same = from === to;

  // A material floor move is judged by the cell's OWN derived threshold; if the
  // history cannot support one, a floor move cannot be alerted at all.
  let floorMove = null;
  if (prev && next && prev.netFloor != null && next.netFloor != null && next.materialMove != null) {
    const d = unqd(qd(next.netFloor) - qd(prev.netFloor));
    if (Math.abs(d) >= next.materialMove) floorMove = d;
  }

  const out = { kind: null, email: false, record: false, from, to, floorMove, reason: null };

  if (to === DECISION.NO_READ && from && from !== DECISION.NO_READ) {
    out.kind = ALERT.DATA_STALE; out.email = true; out.record = true;
    out.reason = (next.dataQuality && next.dataQuality.reasons && next.dataQuality.reasons[0]) || "data gate closed";
    return out;
  }
  if (to === DECISION.BUY && from !== DECISION.BUY) {
    out.kind = ALERT.ENTER_BUY; out.email = true; out.record = true; out.reason = next.why; return out;
  }
  if (to === DECISION.START && from !== DECISION.START && from !== DECISION.BUY) {
    out.kind = ALERT.ENTER_START; out.email = true; out.record = true; out.reason = next.why; return out;
  }
  if (acted(from) && !acted(to) && to !== DECISION.NO_READ) {
    out.kind = ALERT.DOWNGRADE; out.email = false; out.record = true;
    out.reason = `${from} → ${to}: ${next.why}`; return out;
  }
  // A floor move is news only while the cell is ACTING (BUY/START). Measured
  // 2026-10-15: 33 of 34 stored floor_move rows were WAIT->WAIT / WATCH->WATCH
  // — noise about quarters nobody was being asked to look at.
  if (same && floorMove != null && acted(to)) {
    out.kind = ALERT.FLOOR_MOVE; out.email = true; out.record = true;
    out.reason = `net floor ${floorMove >= 0 ? "up" : "down"} $${Math.abs(floorMove).toFixed(2)} while holding ${to}`;
    return out;
  }
  return out; // kind null — nothing happened, nothing sent
}

module.exports = {
  MODEL_VERSION, DECISION_VERSION, DEFAULT_THRESHOLDS, DECISION, DATA_QUALITY, ALERT,
  thresholdVersion, parseDrpQuarter, drpQKey, buildFloorHistory, drpDecide, drpAlertTransition,
  sameRuleVersion, previousDecisionInput,
};
